import React, { useEffect, useState, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { CustomRightHeaderPanel } from 'layouts/components';
import { currentUser } from 'redux/session/selectors';
import {
  cancelUploadShapeFile,
  initialAOIPageStart,
  unloadAOIPage
} from 'redux/areaOfInterest';
import { fetchOrgProfileStart, clearOrgProfile } from 'redux/member';
import { Tabs, Tab } from 'carbon-components-react';
import Details from './components/details';
import History from './components/history';
import UploadPanel from './components/uploadPanel';
import ManageBuffer from './components/manageBuffer';
import CommonBoundariesPanel from './components/commonBoundariesPanel';

const AreaOfInterest: React.FC<RouteComponentProps<{}, any, any>> = () => {
  const dispatch: any = useDispatch();
  const user: any = useSelector(currentUser);
  const [organisationId, setOrganisationId] = useState<string>('');
  const [enableHistory, setEnableHistory] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [currentPanel, setCurrentPanel] = useState<string>('upload');
  const [isPannelExpanded, setIsPannelExpanded] = useState<boolean>(false);

  const handleOpenPanel = (panel: string) => {
    setCurrentPanel(panel);
    openPanel(true);
  };

  const handleHistoryReady = useCallback(() => {
    setEnableHistory(true);
  }, []);

  useEffect(() => {
    if (user) {
      const organisationId = user.organisationId.toString();
      setOrganisationId(organisationId);
    }
  }, [dispatch, user]);

  useEffect(() => {
    dispatch(initialAOIPageStart(organisationId));
    if (organisationId) {
      dispatch(
        fetchOrgProfileStart({
          organisationId: Number(organisationId),
          params: {
            include: 'Tenant'
          }
        })
      );
    }
    return () => {
      dispatch(unloadAOIPage());
      dispatch(clearOrgProfile());
    };
  }, [organisationId, dispatch]);

  const openPanel = (bool: boolean) => {
    dispatch(cancelUploadShapeFile());
    setIsPannelExpanded(bool);
  };

  return (
    <div
      id="area-of-interest-page"
      data-testid="areaOfInterestTest"
      className="area-of-interest"
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Area of Interest"
        pageDescription="Configure your AOI to receive referrals relating to your assets"
        enableHeaderGlobalBar={true}
        headerChildren={
          <CustomRightHeaderPanel
            panelHeading={
              currentPanel === 'upload' ? 'Upload AOI' : 'LGA boundaries'
            }
            isExpanded={isPannelExpanded}
            closeButtonHandler={() => openPanel(false)}
            hideCloseButton={true}
          >
            {currentPanel === 'upload' && (
              <UploadPanel onPanelClose={() => openPanel(false)} />
            )}
            {currentPanel === 'boundary' && (
              <CommonBoundariesPanel onPanelClose={() => openPanel(false)} />
            )}
          </CustomRightHeaderPanel>
        }
      >
        <div className="area-of-interest--body">
          <div className="tabs-container">
            <Tabs
              onSelectionChange={(selected: number) => setSelectedTab(selected)}
            >
              <Tab id="aoi" label="AOI">
                {selectedTab === 0 && (
                  <Details
                    organisationId={organisationId}
                    openPanel={handleOpenPanel}
                    historyReady={handleHistoryReady}
                  />
                )}
              </Tab>
              <Tab
                id="aoiHistory"
                label="AOI History"
                disabled={!enableHistory}
              >
                {selectedTab === 1 && (
                  <History organisationId={organisationId} />
                )}
              </Tab>
              <Tab id="manageBuffer" label="Manage buffer">
                {selectedTab === 2 && (
                  <ManageBuffer organisationId={organisationId} />
                )}
              </Tab>
            </Tabs>
          </div>
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};
AreaOfInterest.displayName = 'AreaOfInterestModule';

export default AreaOfInterest;
